import { useToast } from '@razorpay/blade/components';
import api from 'api';
import { useMutation } from 'react-query';
import { AppError } from 'utils/AppError';
import { MetaInfo, AssignedFlexibleComponentsTableDataType } from '../types';
import { rupeeAmountToPaise } from 'utils/Numbers';
import { useCallback, useState } from 'react';
import {
  getPercentageValue,
  getSelectionChanges,
  getUpdatedAssignments,
  isFBPUnitAmount,
} from '../utils';
import {
  CreateAssignmentInputVariables,
  DeclareValueInputVariables,
  MutationBulkRemoveFlexibleBenefitsResponse,
  MutationCreateBulkAssignmentResponse,
  RemoveAssignmentIdType,
  UpdateAssignmentInputVariables,
} from 'api/gql-utils/flexibleBenefits/types';
import { EMPLOYER_NPS_COMPONENT_NAME } from '../constants';
import { useEmployeeSalaryDetails } from './useEmployeeSalaryDetails';

interface useAllocateMutationsInterface {
  selectedIds: string[];
  monthlyDeclarations: Record<string, string>;
  previousSelectedIds: string[];
  previousMonthlyDeclarations: Record<string, string>;
  meta: MetaInfo;
  invalidateQueries: () => Promise<void>;
  flexibleComponentsData?: AssignedFlexibleComponentsTableDataType[];
  onSuccess?: () => void;
  isAdmin: boolean;
  closeConfirmationModal: () => void;
}

export const useAllocateMutations = ({
  selectedIds,
  monthlyDeclarations,
  previousSelectedIds,
  previousMonthlyDeclarations,
  meta,
  invalidateQueries,
  flexibleComponentsData,
  onSuccess,
  isAdmin,
  closeConfirmationModal,
}: useAllocateMutationsInterface) => {
  const toast = useToast();
  const [isRunning, setIsRunning] = useState(false);
  const { totalBase } = useEmployeeSalaryDetails();
  const createMutation = useMutation({
    mutationKey: ['create_flexible_assignment'],
    mutationFn: api.flexibleBenefitComponents.mutations.createBulkFlexibleComponentsAssignments,
  });

  const updateMutation = useMutation({
    mutationKey: ['update_flexible_assignment'],
    mutationFn: api.flexibleBenefitComponents.mutations.updateBulkFlexibleComponentsAssignments,
    onSuccess: () => {},
  });

  const removeMutation = useMutation({
    mutationKey: ['remove_flexible_assignment'],
    mutationFn: api.flexibleBenefitComponents.mutations.removeBulkFlexibleComponentsAssignments,
  });

  const declareValueMutation = useMutation({
    mutationKey: ['declare_value'],
    mutationFn: api.flexibleBenefitComponents.mutations.declareBulkComponentsValues,
    onSuccess: () => {
      setIsRunning(false);
      toast.show({ content: 'FBP declaration Successful', color: 'positive' });
      invalidateQueries();
      onSuccess?.();
    },
    onError: (error: AppError) => {
      setIsRunning(false);
      closeConfirmationModal();
      toast.show({ content: error.message, color: 'negative' });
    },
  });

  const buildDeclareValuePayload = (
    updatedAssignments: Record<string, string>,
    meta: MetaInfo,
  ): DeclareValueInputVariables[] =>
    Object.entries(updatedAssignments).map(([id]) => {
      const component = flexibleComponentsData?.find((comp) => comp.id === id);
      const isAmount = isFBPUnitAmount(component?.unit);

      const value = isAmount
        ? rupeeAmountToPaise(Number(meta.getValue(id)))?.toString()
        : meta.getValue(id);

      return {
        assignment_id: component?.assignment_id || '',
        declared_value: value,
      };
    });

  const buildCreatePayload = (
    addedIds: string[],
    meta: MetaInfo,
  ): CreateAssignmentInputVariables[] =>
    addedIds.map((id) => {
      const component = flexibleComponentsData?.find((comp) => comp.id === id);
      const isAmount = isFBPUnitAmount(component?.unit);

      const value = isAmount
        ? rupeeAmountToPaise(Number(meta.getValue(id)))?.toString()
        : meta.getValue(id);
      return {
        component_id: id,
        user_id: meta?.userId?.toString() || '',
        component_value: value,
        effective_from: meta.now,
        effective_to: '-1',
      };
    });

  const buildUpdatePayload = (
    updatedAssignments: Record<string, string>,
    meta: MetaInfo,
  ): UpdateAssignmentInputVariables[] =>
    Object.entries(updatedAssignments).map(([id]) => {
      const component = flexibleComponentsData?.find((comp) => comp.id === id);
      const isAmount = isFBPUnitAmount(component?.unit);

      const value = isAmount
        ? rupeeAmountToPaise(Number(meta.getValue(id)))?.toString()
        : meta.getValue(id);

      return {
        assignment_id: component?.assignment_id || '',
        component_value: value,
        effective_from: meta.now,
        effective_to: '-1',
      };
    });

  const buildRemovePayload = (removedIds: string[], meta: MetaInfo): RemoveAssignmentIdType[] =>
    removedIds.map((id) => {
      const component = flexibleComponentsData?.find((comp) => comp.id === id);
      return component?.assignment_id || '';
    });

  const componentAssignmentOps = useCallback(() => {
    const { removedIds, addedIds, unchangedIds } = getSelectionChanges({
      currentSelection: selectedIds,
      previousSelection: previousSelectedIds,
    });

    const updatedAssignments = getUpdatedAssignments({
      currentMonthlyDeclaration: monthlyDeclarations,
      previousMonthlyDeclaration: previousMonthlyDeclarations,
      assignedIds: unchangedIds,
    });

    return {
      createIds: addedIds,
      updateIds: Object.keys(updatedAssignments),
      removeIds: removedIds,
      updatedAssignments,
    };
  }, [selectedIds, monthlyDeclarations, previousSelectedIds, previousMonthlyDeclarations]);

  const declareFlexibleComponents = async (
    updatedAssignments: Record<string, string>,
    meta: MetaInfo,
  ) => {
    const declareValuePayload = buildDeclareValuePayload(updatedAssignments, meta);
    setIsRunning(true);
    await declareValueMutation.mutateAsync({ declarations: declareValuePayload });
  };

  const processAllocations = async ({
    addedIds,
    updatedAssignments = {},
    removedIds,
    meta,
  }: {
    addedIds: string[];
    updatedAssignments?: Record<string, string>;
    removedIds: string[];
    meta: MetaInfo;
  }): Promise<{ failed: boolean; partialSucceeded: boolean; failedErrors: string[] }> => {
    const promises: Promise<
      MutationCreateBulkAssignmentResponse | MutationBulkRemoveFlexibleBenefitsResponse
    >[] = [];
    if (addedIds.length) {
      const createPayload = buildCreatePayload(addedIds, meta);
      promises.push(createMutation.mutateAsync({ assignments: createPayload }));
    }

    if (Object.keys(updatedAssignments).length) {
      const updatePayload = buildUpdatePayload(updatedAssignments, meta);
      promises.push(updateMutation.mutateAsync({ assignments: updatePayload }));
    }

    if (removedIds.length) {
      const removePayload = buildRemovePayload(removedIds, meta);
      promises.push(removeMutation.mutateAsync({ assignmentIds: removePayload }));
    }

    const results = await Promise.allSettled(promises);
    const failed = results.every((r) => r.status === 'rejected');

    const passed = results.every((r) => r.status === 'fulfilled');

    const failedErrors = results.reduce((errors, errorResult) => {
      if (errorResult.status === 'rejected') {
        const errorMessage: string = errorResult.reason.details.messageForUser ?? '';
        errors.push(errorMessage);
      }
      return errors;
    }, [] as string[]);
    const partialSucceeded = !failed && !passed;
    return { failed, partialSucceeded, failedErrors };
  };

  const checkForErrors = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    flexibleComponentsData?.forEach((comp) => {
      if (selectedIds.includes(comp.id) && monthlyDeclarations.hasOwnProperty(comp.id)) {
        const maxValuePerMonth = Number(comp.maxValuePerMonth);
        const type = comp.monthlyDeclaration.type;
        if (!monthlyDeclarations[comp.id]) {
          errors[comp.id] = 'Monthly declaration value can not be empty';
          return;
        }
        const value = Number(monthlyDeclarations[comp.id]);
        if (type === 'FIXED' && maxValuePerMonth !== value) {
          errors[comp.id] = 'Value must be equal to max value for fixed components';
        } else if (type === 'LIST_OF_OPTION') {
          const options = comp.monthlyDeclaration.options;
          const hasValue = options.find((option) => Number(option.value) === value);
          if (!hasValue)
            errors[comp.id] = 'Value must be less than or equal to max value for list components';
        } else if (type === 'RANGE' && (value > maxValuePerMonth || value < 0)) {
          errors[comp.id] = 'Value must be less than or equal to max value for range components';
        }
        if (comp.component_name === EMPLOYER_NPS_COMPONENT_NAME) {
          const npsValueInRupees = getPercentageValue(totalBase, value);
          const monthlyNpsValue = npsValueInRupees / 12;
          if (monthlyNpsValue < 500) {
            errors[comp.id] =
              'Value must be greater than or equal to 500 for employer NPS component';
          }
        }
      }
    });
    return errors;
  };

  const handleAllocateClick = async () => {
    const currentOps = componentAssignmentOps();
    if (!isAdmin) {
      await declareFlexibleComponents(currentOps.updatedAssignments, meta);
      return;
    }
    setIsRunning(true);
    const { failed, partialSucceeded, failedErrors } = await processAllocations({
      addedIds: currentOps.createIds,
      removedIds: currentOps.removeIds,
      updatedAssignments: currentOps.updatedAssignments,
      meta,
    });

    if (failedErrors.length) {
      failedErrors.forEach((error) => {
        toast.show({ content: error, color: 'negative', duration: 5000 });
      });
    }
    if (partialSucceeded) {
      await invalidateQueries();
      toast.show({
        content: 'Some of the operations failed. Please try again to finish them',
        color: 'negative',
        duration: 5000,
      });
    } else if (failed) {
      toast.show({ content: 'Failed to save the data', color: 'negative' });
    } else {
      invalidateQueries();
      toast.show({ content: 'FBP allocation and declaration successful', color: 'positive' });
      onSuccess?.();
    }
    setIsRunning(false);
    closeConfirmationModal();
  };

  const hasEmployerNPSComponentSelected = (): boolean => {
    const { createIds, updateIds } = componentAssignmentOps();
    const npsComponent = flexibleComponentsData?.find(
      (comp) => comp.component_name === EMPLOYER_NPS_COMPONENT_NAME,
    );
    if (!npsComponent) return false;
    let hasUpdated = false;
    if (isAdmin) {
      hasUpdated = createIds.includes(npsComponent.id) && meta.getValue(npsComponent.id) !== '0';
    }

    hasUpdated =
      hasUpdated || (updateIds.includes(npsComponent.id) && meta.getValue(npsComponent.id) !== '0');

    return hasUpdated;
  };

  return {
    processAllocations,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isRemoving: removeMutation.isLoading,
    isRunning,
    handleAllocateClick,
    buildOps: componentAssignmentOps,
    checkForErrors,
    hasEmployerNPSComponentSelected,
  };
};
